// Static content banks for the homepage. The Accueil view picks each day's
// item via day-of-year rotation. Vocab deck is loaded dynamically from
// /data/vocab-deck.json; per-track decks live alongside.

export type CefrSubLevel = 'A1.1' | 'A1.2' | 'A1.3' | 'A1.4' | 'A2.1' | 'A2.2' | 'A2.3' | 'A2.4' | 'B1.1' | 'B1.2' | 'B1.3' | 'B1.4';

export interface TrackCard {
  id: 'vocab' | 'grammar' | 'pron' | 'listen' | 'task';
  title: string;
  subtitle: string;
  detail: string;
  minutes: number;
  cefrTag: string;
  tint: 'sage' | 'peach' | 'mint' | 'butter' | 'rose';
}

export interface ContrastNote {
  word: string;
  trap: string;
  examples: { norwegian: string; french: string }[];
}

export interface DialectNote {
  bokmal: string;
  bokmalGloss: string;
  stavangersk: string;
}

const LEVEL_LABEL: Record<CefrSubLevel, string> = {
  'A1.1': 'Premiers contacts',
  'A1.2': 'Vie quotidienne de base',
  'A1.3': 'Se présenter et décrire son quotidien',
  'A1.4': 'Interactions simples du jour le jour',
  'A2.1': 'Autonomie pratique en Norvège',
  'A2.2': 'Aisance sociale',
  'A2.3': 'Descriptions et expériences',
  'A2.4': 'Prêt pour le monde du travail',
  'B1.1': 'Indépendance émergente',
  'B1.2': 'Usager confiant',
  'B1.3': 'Aisance dans la durée',
  'B1.4': 'Vers le B2',
};

export function labelForLevel(level: CefrSubLevel): string {
  return LEVEL_LABEL[level];
}

export const overallGoal = 'communiquer avec aisance dans la vie quotidienne en Norvège';

export const tracks: TrackCard[] = [
  { id: 'vocab',   title: 'Vocabulaire',   subtitle: 'FSRS',                 detail: '2 000 mots les plus fréquents',                  minutes: 15, cefrTag: 'A1–B1', tint: 'sage' },
  { id: 'grammar', title: 'Grammaire',     subtitle: 'cloze',                detail: '35 patterns clés',                              minutes: 15, cefrTag: 'A1–B1', tint: 'peach' },
  { id: 'pron',    title: 'Prononciation', subtitle: 'HVPT',                 detail: '28 paires minimales · 6 contrastes',             minutes: 10, cefrTag: 'A1–A2', tint: 'mint' },
  { id: 'listen',  title: 'Écoute',        subtitle: 'bokmål + siddis',      detail: '15 documents · transcriptions',                  minutes: 20, cefrTag: 'A1–B1', tint: 'butter' },
  { id: 'task',    title: 'Tâche hebdo',   subtitle: 'CEFR',                 detail: '1 tâche écrite ou orale par semaine',            minutes: 60, cefrTag: 'A1–B1', tint: 'rose' },
];

// --- Contrast bank (cycles daily) ---

export const CONTRAST_BANK: ContrastNote[] = [
  { word: 'by',
    trap: 'En norvégien, « by » signifie *ville*, pas « par ». Faux ami fréquent.',
    examples: [
      { norwegian: 'en by',         french: 'une ville' },
      { norwegian: 'å by på noe',   french: 'offrir quelque chose' },
    ] },
  { word: 'gift',
    trap: '« Gift » a deux sens : *marié* (adjectif) et *poison* (nom). Le contexte décide.',
    examples: [
      { norwegian: 'Hun er gift',    french: 'Elle est mariée.' },
      { norwegian: 'rottegift',      french: 'mort-aux-rats (litt. poison à rat)' },
    ] },
  { word: 'rar',
    trap: '« Rar » signifie *bizarre*, pas « rare ». Pour rare, on dit *sjelden*.',
    examples: [
      { norwegian: 'Det er litt rart', french: 'C’est un peu bizarre.' },
      { norwegian: 'en sjelden fugl',  french: 'un oiseau rare' },
    ] },
  { word: 'snill',
    trap: '« Snill » = *gentil*, *sage*. Souvent confondu avec l’anglais *sneaky*.',
    examples: [
      { norwegian: 'Vær snill mot katten', french: 'Sois gentil avec le chat.' },
      { norwegian: 'et snilt barn',         french: 'un enfant sage' },
    ] },
  { word: 'middag',
    trap: '« Middag » se prend vers 16h–17h en Norvège, pas à midi. Le déjeuner s’appelle *lunsj*.',
    examples: [
      { norwegian: 'Vi spiser middag klokken fem', french: 'On dîne à 17 heures.' },
      { norwegian: 'lunsjpause',                    french: 'pause déjeuner' },
    ] },
  { word: 'fart',
    trap: '« Fart » signifie *vitesse*, pas un mot grossier. Très visible sur les panneaux routiers.',
    examples: [
      { norwegian: 'Hold farten!',  french: 'Maintiens la vitesse !' },
      { norwegian: 'høy fart',       french: 'grande vitesse' },
    ] },
  { word: 'kjekt',
    trap: '« Kjekt » à l’Est = *agréable, sympa*. À Stavanger, *kjekk* (pour une personne) = beau / élégant.',
    examples: [
      { norwegian: 'Det var kjekt å se deg', french: 'C’était sympa de te voir.' },
      { norwegian: 'en kjekk mann',           french: 'un bel homme' },
    ] },
];

// --- Stavanger siddis bank (cycles daily) ---

export const SIDDIS_BANK: DialectNote[] = [
  { bokmal: 'Hvordan har du det?',      bokmalGloss: 'Comment vas-tu ?',                  stavangersk: 'Kordan har du det?' },
  { bokmal: 'Hva heter du?',             bokmalGloss: 'Comment tu t’appelles ?',           stavangersk: 'Ka heter du?' },
  { bokmal: 'Hvor bor du?',              bokmalGloss: 'Où habites-tu ?',                   stavangersk: 'Kor bor du?' },
  { bokmal: 'Jeg vet ikke',              bokmalGloss: 'Je ne sais pas.',                   stavangersk: 'Eg vet ikkje.' },
  { bokmal: 'Jeg er på vei',             bokmalGloss: 'Je suis en chemin.',                stavangersk: 'Eg er på vei.' },
  { bokmal: 'Hva sa du?',                bokmalGloss: 'Qu’as-tu dit ?',                    stavangersk: 'Ka sa du?' },
  { bokmal: 'Vi sees i morgen',          bokmalGloss: 'On se voit demain.',                stavangersk: 'Vi ses i morgå.' },
];

// --- Ordtak bank (cycles daily) ---

export const ORDTAK_BANK: { norwegian: string; french: string }[] = [
  { norwegian: 'Det er ikke farlig å gå sakte, bare man ikke stopper opp.',
    french: 'Il n’est pas dangereux d’avancer lentement, tant qu’on ne s’arrête pas.' },
  { norwegian: 'Smaken er som baken — delt.',
    french: 'Les goûts sont comme le derrière — divisés (chacun les siens).' },
  { norwegian: 'Det er bedre å være forut for sin tid enn etter.',
    french: 'Mieux vaut être en avance sur son temps qu’en retard.' },
  { norwegian: 'Tidlig krøkes det som god krok skal bli.',
    french: 'On forme tôt celui qu’on veut bien tordre (≈ on apprend jeune).' },
  { norwegian: 'Etter regn kommer sol.',
    french: 'Après la pluie vient le soleil.' },
  { norwegian: 'Mange bekker små gjør en stor å.',
    french: 'Beaucoup de petits ruisseaux font une grande rivière.' },
  { norwegian: 'Den som intet våger, intet vinner.',
    french: 'Qui ne risque rien n’a rien.' },
];

function dayOfYear(d = new Date()): number {
  const start = Date.UTC(d.getUTCFullYear(), 0, 0);
  const now = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.floor((now - start) / 86_400_000);
}

export function contrastOfDay(d = new Date()): ContrastNote {
  return CONTRAST_BANK[dayOfYear(d) % CONTRAST_BANK.length]!;
}

export function siddisOfDay(d = new Date()): DialectNote {
  return SIDDIS_BANK[dayOfYear(d) % SIDDIS_BANK.length]!;
}

export function ordtakOfDay(d = new Date()): { norwegian: string; french: string } {
  return ORDTAK_BANK[dayOfYear(d) % ORDTAK_BANK.length]!;
}
